/**
 * Multi-Client API Manager
 * Manages multiple Axios instances for different API endpoints
 * Each client can have its own base URL and interceptors
 */

import axios, { type AxiosInstance } from "axios";
import { setupInterceptors, type InterceptorConfig } from "./interceptors";
import type { ApiConfig } from "./api-config";

export interface ApiClientConfig extends Omit<InterceptorConfig, "baseURL"> {
  apiConfig: ApiConfig;
}

/**
 * Manages multiple API clients for different endpoints
 */
export class ApiClientManager {
  private clients: Map<string, AxiosInstance> = new Map();
  private config: ApiClientConfig;
  private defaultClientName: string;

  constructor(config: ApiClientConfig) {
    this.config = config;
    this.defaultClientName = config.apiConfig.defaultEndpoint;
    this.initializeClients();
  }

  private initializeClients(): void {
    const { apiConfig, ...interceptorConfig } = this.config;

    Object.entries(apiConfig.endpoints).forEach(([name, endpoint]) => {
      const client = axios.create({
        baseURL: endpoint.baseURL,
        timeout: this.config.timeout || 30000,
        headers: {
          "Content-Type": "application/json",
        },
      });

      setupInterceptors(client, {
        ...interceptorConfig,
        baseURL: endpoint.baseURL,
      });

      this.clients.set(name, client);
    });
  }

  /**
   * Get a specific API client by name
   * @param name - Client name (uses default if not provided)
   */
  getClient(name?: string): AxiosInstance {
    const clientName = name || this.defaultClientName;
    const client = this.clients.get(clientName);

    if (!client) {
      throw new Error(
        `API client "${clientName}" not found. Available clients: ${Array.from(this.clients.keys()).join(", ")}`,
      );
    }

    return client;
  }

  getAllClients(): Map<string, AxiosInstance> {
    return new Map(this.clients);
  }

  getDefaultClient(): AxiosInstance {
    return this.getClient();
  }

  hasClient(name: string): boolean {
    return this.clients.has(name);
  }

  getClientNames(): string[] {
    return Array.from(this.clients.keys());
  }
}

/**
 * Global API client manager instance
 * Can be initialized once and used throughout the app
 */
let globalApiClientManager: ApiClientManager | null = null;

/**
 * Initialize the global API client manager
 */
export function initializeApiClients(
  config: ApiClientConfig,
): ApiClientManager {
  if (globalApiClientManager) {
    console.warn(
      "[API] Global API client manager already initialized. Reinitializing...",
    );
  }
  globalApiClientManager = new ApiClientManager(config);
  return globalApiClientManager;
}

/**
 * Get the global API client manager
 */
export function getApiClientManager(): ApiClientManager {
  if (!globalApiClientManager) {
    throw new Error(
      "API client manager not initialized. Call initializeApiClients() first.",
    );
  }
  return globalApiClientManager;
}

/**
 * Get a client from the global manager
 */
export function getApiClient(name?: string): AxiosInstance {
  return getApiClientManager().getClient(name);
}
